import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { ADS_ENABLED } from "@/const";
import { usePaywallDialogOpen } from "@/hooks/usePaywallDialog";
import { useGuestCheckoutPlan } from "@/hooks/useGuestCheckout";
import { applyNoAdsFlag } from "@/lib/adPlacement";
import { OPEN_CONSENT_EVENT, loadAllowedScripts, setPlanAllowsAds, readConsent, visitorIsRegulated, writeConsent, type Consent } from "@/lib/consent";

/** Asks visitors in the EEA, UK and Switzerland before Analytics or ad scripts load. Hidden elsewhere; "Cookie settings" in the footer reopens it. */
export function ConsentBanner() {
  const [regulated] = useState(visitorIsRegulated);
  const [stored, setStored] = useState<Consent | null>(() => readConsent());
  const [open, setOpen] = useState(false);
  // First visit: a slim bar. "Choose" (or the footer's Cookie settings link) expands the per-category switches.
  const [expanded, setExpanded] = useState(false);
  const [location] = useLocation();
  const paywallDialogOpen = usePaywallDialogOpen();
  const guestDialogOpen = useGuestCheckoutPlan() !== null;
  const paywallOpen = paywallDialogOpen || guestDialogOpen;
  const [analytics, setAnalytics] = useState(stored?.analytics ?? false);
  const [ads, setAds] = useState(stored?.ads ?? false);

  // Ads only for visitors who are not Pro subscribers; wait for a definite answer so Pro users never load the ad script.
  const { isAuthenticated, loading } = useAuth();
  const billing = trpc.billing.status.useQuery(undefined, { enabled: isAuthenticated });
  const planKnown = !loading && (!isAuthenticated || !billing.isLoading);
  const planAllowsAds = ADS_ENABLED && !billing.data?.isPro;

  // Load whatever the current choice and plan allow (everything outside covered regions), once the plan is known.
  useEffect(() => {
    if (!planKnown) return;
    setPlanAllowsAds(planAllowsAds);
    loadAllowedScripts(regulated, stored);
  }, [planKnown, planAllowsAds, location]); // eslint-disable-line react-hooks/exhaustive-deps
  // Page-level switch: hides any automatic ad Google already placed once the visitor reaches a payment, sign-in, account or API-key page.
  useEffect(() => { applyNoAdsFlag(location); }, [location]);
  useEffect(() => { if (regulated && !stored) setOpen(true); }, [regulated, stored]);
  useEffect(() => {
    const h = () => { setAnalytics(readConsent()?.analytics ?? false); setAds(readConsent()?.ads ?? false); setExpanded(true); setOpen(true); };
    window.addEventListener(OPEN_CONSENT_EVENT, h);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, h);
  }, []);

  const choose = (a: boolean, d: boolean) => {
    const prevAllowedMore = (stored?.analytics && !a) || (stored?.ads && !d);
    const c = writeConsent({ analytics: a, ads: d });
    setStored(c); setOpen(false);
    if (prevAllowedMore) { window.location.reload(); return; } // withdrawing: start clean, nothing from the old choice keeps running
    loadAllowedScripts(regulated, c);
    window.dispatchEvent(new Event("fdp-consent-saved"));
  };

  // Never layer over the paywall dialog: stay hidden while it is open (the choice is not lost; the banner returns after it closes if still undecided).
  if (!open || paywallOpen) return null;
  return (
    <div role="dialog" aria-label="Cookie settings" className="fixed inset-x-0 bottom-0 z-[60] border-t bg-background px-3 py-2 shadow-lg sm:px-4">
      <div className="mx-auto max-w-4xl text-xs sm:text-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <p className="flex-1 leading-snug">
            We use cookies for analytics and, when ads are shown, advertising. The tools work either way.
            {" "}<Link href="/privacy" className="underline">Privacy</Link>
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => choose(true, true)}>Accept all</Button>
            <Button size="sm" variant="outline" onClick={() => choose(false, false)}>Reject non-essential</Button>
            {!expanded && <Button size="sm" variant="ghost" onClick={() => setExpanded(true)}>Choose</Button>}
          </div>
        </div>
        {expanded && (
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-2">
            <label className="flex items-center gap-2 opacity-70"><input type="checkbox" checked disabled /> Essential (sign-in, saved choices): always on</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} /> Analytics (Google Analytics)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={ads} onChange={(e) => setAds(e.target.checked)} /> Advertising (Google AdSense, Infolinks)</label>
            <Button size="sm" variant="secondary" onClick={() => choose(analytics, ads)}>Save my choices</Button>
          </div>
        )}
      </div>
    </div>
  );
}
